// @flow
import BaseService from "./BaseService";
import AppConfig from "../config";
import type {ProductVersionsResultType, SessionResultType, SessionType, UserInfoType, WagonsResultType} from "../model";


/**
 * Services class for making KLPMS REST API requests.
 * Use like:
 * ```javascript
 * let service = new KlpmsService();
 * try {
 *     const data = await service.logon(username, password);
 * } catch (error: Error) {
 *     if (error instanceof ServiceError) {
 *         // HTTP-level error (that is - HTTP status code 4xx or 5xx)
 *         let response: Response = error.response;
 *     } else {
 *         // Low-level error (e.g. unable to connect)
 *         let name: string = error.name;
 *         let message: string = error.message;
 *         let stack: string = error.stack;
 *     }
 * }
 * ```
 */
class KlpmsService extends BaseService {
    constructor() {
        super(AppConfig.BASE_URL);
    }

    async getProductVersions(codeVariant: string): Promise<ProductVersionsResultType> {
        return this.getData("productVersions/" + codeVariant);
    }

    async getWagon(nr: number): Promise<WagonsResultType> {
        return this.getData("wagons/" + nr.toString());
    }

    async getWagonCurrent(): Promise<WagonsResultType> {
        return this.getData("wagons/current");
    }

    async getWagonManual(): Promise<WagonsResultType> {
        return this.getData("wagons/manual");
    }

    async postManualMeasuring(codeVariant: string): Promise<{}> {
        return this.postData("manualMeasuring", {kood: codeVariant});
    }

    /**
     * Logs the user in
     * @param username Username
     * @param password Password
     * @returns {Promise<SessionResultType>}
     */
    async logon(username: string, password: string): Promise<SessionResultType> {
        const userInfo: UserInfoType = {
            username,
            password
        };
        return this.postData("logon", userInfo);
    }

    async logoff(): Promise<{}> {
        return this.postData("logoff", null);
    }
}


export default KlpmsService;
